import { useCallback, useEffect, useState } from 'react'
import { userRepository } from '../../data/repositories/UserRepositoryImpl'
import { User, UserStatus } from '../../domain/entities/User'

export const useUsers = (initialLimit = 10) => {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [role, setRole] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await userRepository.getUsers({
        search: search || undefined,
        role: role || undefined,
        page,
        limit: initialLimit,
      })
      setUsers(res.users)
      setTotalPages(res.totalPages)
      setTotalItems(res.totalItems)
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to load users.')
    } finally {
      setLoading(false)
    }
  }, [search, role, page, initialLimit])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const createUser = async (user: Omit<User, 'id' | 'keycloakId' | 'status' | 'createdAt' | 'updatedAt'>) => {
    setError(null)
    try {
      await userRepository.createUser(user)
      await fetchUsers()
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to create user.')
      throw err
    }
  }

  const updateUser = async (id: string, user: Partial<Omit<User, 'id' | 'keycloakId' | 'createdAt' | 'updatedAt'>>) => {
    setError(null)
    try {
      await userRepository.updateUser(id, user)
      await fetchUsers()
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to update user.')
      throw err
    }
  }

  const toggleStatus = async (id: string, currentStatus: UserStatus) => {
    setError(null)
    try {
      const nextStatus: UserStatus = currentStatus === 'active' ? 'inactive' : 'active'
      await userRepository.toggleUserStatus(id, nextStatus)
      await fetchUsers()
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to update user status.')
      throw err
    }
  }

  const resetFilters = () => {
    setSearch('')
    setRole('')
    setPage(1)
  }

  return {
    users,
    loading,
    error,
    search,
    setSearch,
    role,
    setRole,
    page,
    setPage,
    totalPages,
    totalItems,
    createUser,
    updateUser,
    toggleStatus,
    resetFilters,
    refresh: fetchUsers,
  }
}
